import { useQuery } from '@apollo/client';
import { Loader, Tooltip } from '@teamsnap/teamsnap-ui';
import * as React from 'react';

import * as QueryFormSummary from 'core/gql/queries/show-form-summary';

import { formatNumberToAmountWithCommasAndTwoDecimals } from 'core/format';
import './ParticipantSummaryPanel.scss';

interface Props {
  organizationId: number;
  formId: number;
}

const ParticipantSummaryPanel = ({ organizationId, formId }: Props) => {
  const { loading, data } = useQuery(QueryFormSummary.QUERY, {
    variables: { formId: +formId, organizationId: +organizationId },
  });

  return (
    <>
      {loading && <Loader type="spin" text="loading..." />}

      {data && (
        <div className="ParticipantSummaryPanel d:sui-flex sui-gap-2">
          <div 
            className="d:sui-w-2/4 sui-p-2 sui-border sui-border-solid sui-border-neutral-border sui-rounded sui-mb-2"
            style={{ boxShadow: '0 10px 15px 0 rgba(0, 0, 255, 0.03)' }} // to match the subtle shadow of the original design
          >
            <div
              className="sui-font-size-6 sui-pb-3"
            >
              Participants
            </div>
            <div className="sui-flex sui-justify-between">
              <div className="sui-flex sui-items-center sui-flex-col">
                <span className="report-summary-panel__label">Completed</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold">
                    {data?.showFormSummary?.participant_count}
                  </span>
                </div>
              </div>
              <div className="sui-flex sui-items-center sui-flex-col">
                <span className="report-summary-panel__label">In Progress</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold">
                    {data?.showFormSummary?.participant_in_progress}
                  </span>
                </div>
              </div>
              <div className="sui-flex sui-items-center sui-flex-col">
                <span className="report-summary-panel__label">In Cart</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold">
                    {data?.showFormSummary?.participant_in_cart}
                  </span>
                </div>
              </div>

              <div className="sui-flex sui-items-center sui-flex-col sui-mr-4">
                <span className="report-summary-panel__label">Waitlisted</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold">
                    {data?.showFormSummary?.participant_in_waitlist}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="d:sui-w-2/4 sui-p-2 sui-border sui-border-solid sui-border-neutral-border sui-rounded sui-mb-2"
            style={{ boxShadow: '0 10px 15px 0 rgba(0, 0, 255, 0.03)' }} // to match the subtle shadow of the original design
          >
            <div className="sui-font-size-6 sui-pb-3">Payments</div>
            <div className='sui-flex sui-justify-between'>
              <div className="sui-w-3/12">
                <span className="report-summary-panel__label">Total Fees</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <Tooltip
                    text={`$${formatNumberToAmountWithCommasAndTwoDecimals(
                      data?.showFormSummary?.total_fees_due ?? 0
                    )}`}
                    mods="sui-w-full"
                  >
                    <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold sui-truncate sui-pr-1">
                      $
                      {formatNumberToAmountWithCommasAndTwoDecimals(
                        data?.showFormSummary?.total_fees_due ?? 0
                      )}
                    </span>
                  </Tooltip>
                </div>
              </div>

              <div className="sui-w-3/12">
                <span className="report-summary-panel__label">Collected</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <Tooltip
                    text={`$${formatNumberToAmountWithCommasAndTwoDecimals(
                      data?.showFormSummary?.collected ?? 0
                    )}`}
                    mods="sui-w-full"
                  >
                    <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold sui-truncate sui-pr-1">
                      $
                      {formatNumberToAmountWithCommasAndTwoDecimals(
                        data?.showFormSummary?.collected ?? 0
                      )}
                    </span>
                  </Tooltip>
                </div>
              </div>

              <div className="sui-w-3/12">
                <span className="report-summary-panel__label">Refunds</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <Tooltip
                    text={`$${formatNumberToAmountWithCommasAndTwoDecimals(
                      data?.showFormSummary?.total_refunds ?? 0
                    )}`}
                    mods="sui-w-full"
                  >
                    <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold sui-truncate sui-pr-1">
                      $
                      {formatNumberToAmountWithCommasAndTwoDecimals(
                        data?.showFormSummary?.total_refunds ?? 0
                      )}
                    </span>
                  </Tooltip>
                </div>
              </div>

              <div className="sui-w-3/12">
                <span className="report-summary-panel__label">Balance</span>
                <div className="sui-flex sui-items-center sui-justify-between">
                  <Tooltip
                    text={`$${formatNumberToAmountWithCommasAndTwoDecimals(
                      data?.showFormSummary?.balance ?? 0
                    )}`}
                    mods="sui-w-full"
                  >
                    <span className="report-summary-panel__value sui-text-mobile-5 xl:sui-text-desktop-8 sui-font-bold sui-truncate sui-pr-1">
                      $
                      {formatNumberToAmountWithCommasAndTwoDecimals(
                        data?.showFormSummary?.balance ?? 0
                      )}
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ParticipantSummaryPanel;
